import { getDesks, getMapObjects, type TiledMap } from '@veriverse/map-format';
import { TILE_SIZE } from '@veriverse/shared';

/**
 * Birthday decorations (spec §6.13): balloons tied to the birthday person's desk and a cake in the
 * kitchen, at the map's `cake_spot`. A map without a cake spot gets the cake on the desk instead,
 * next to the balloons. Pure placement maths; `birthday-layer.ts` draws them.
 */

export type DecorationKind = 'balloons' | 'cake';

export interface Decoration {
  readonly kind: DecorationKind;
  /** Bottom-centre of the picture, in pixels. */
  readonly x: number;
  readonly y: number;
  /** Floor row it stands on, for depth sorting against characters. */
  readonly row: number;
}

/** A desk's furniture rectangle in tiles (see `getDesks`). */
export interface DeskArea {
  readonly objectId: number;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface DeskOwner {
  readonly userId: string;
  readonly deskObjectId: number | null;
}

/** Balloon strings end this far in from the desk's right edge, on its top row. */
const BALLOON_INSET = 8;
/** The cake stands a little left of the desk's centre, clear of the balloons. */
const CAKE_DESK_SHIFT = -6;

export function deskAreas(map: TiledMap): DeskArea[] {
  const footprints = new Map(getMapObjects(map).map((object) => [object.id, object.footprint]));
  return getDesks(map).map((desk) => {
    const area = footprints.get(desk.objectId);
    return { objectId: desk.objectId, ...desk.tile, w: area?.w ?? 1, h: area?.h ?? 1 };
  });
}

/** Tiles of the map's `cake_spot` objects (where the birthday cake stands). */
export function cakeSpots(map: TiledMap): { x: number; y: number }[] {
  return getMapObjects(map)
    .filter((object) => object.type === 'cake_spot')
    .map(({ footprint }) => ({ x: footprint.x, y: footprint.y }));
}

/**
 * What to draw for today's birthdays: balloons on each celebrant's desk and one cake per cake
 * spot (or, with no cake spot, one on each celebrant's desk). Celebrants without a desk still
 * get the cake in the kitchen. Nothing when nobody has a birthday.
 */
export function birthdayDecorations(
  celebrants: ReadonlySet<string>,
  owners: readonly DeskOwner[],
  desks: readonly DeskArea[],
  spots: readonly { x: number; y: number }[],
): Decoration[] {
  if (celebrants.size === 0) return [];
  const byId = new Map(desks.map((desk) => [desk.objectId, desk]));
  const celebrantDesks = new Map<number, DeskArea>();
  for (const owner of owners) {
    if (!celebrants.has(owner.userId) || owner.deskObjectId === null) continue;
    const desk = byId.get(owner.deskObjectId);
    if (desk) celebrantDesks.set(desk.objectId, desk);
  }
  const decorations: Decoration[] = [];
  for (const desk of celebrantDesks.values()) {
    const row = desk.y + desk.h - 1;
    decorations.push({
      kind: 'balloons',
      x: (desk.x + desk.w) * TILE_SIZE - BALLOON_INSET,
      y: (desk.y + 1) * TILE_SIZE - 2,
      row,
    });
    if (spots.length === 0)
      decorations.push({
        kind: 'cake',
        x: (desk.x + desk.w / 2) * TILE_SIZE + CAKE_DESK_SHIFT,
        y: (row + 1) * TILE_SIZE - 2,
        row,
      });
  }
  for (const spot of spots)
    decorations.push({
      kind: 'cake',
      x: (spot.x + 0.5) * TILE_SIZE,
      y: (spot.y + 1) * TILE_SIZE,
      row: spot.y,
    });
  return decorations;
}

/** Dev only: `?birthday` celebrates the signed-in user today, to preview the decorations. */
export function devBirthdayOverride(search: string, isDev: boolean): boolean {
  return isDev && new URLSearchParams(search).has('birthday');
}
